import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Youtube, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SEO from "@/components/SEO";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO title="Reset password | NewStarAds" description="Reset your NewStarAds account password." />
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl"><span className="grid place-items-center w-8 h-8 rounded-lg bg-primary"><Youtube className="w-5 h-5" /></span> NewStarAds</Link>
        <div><h2 className="font-display text-4xl font-bold tracking-tight leading-tight">Forgot your password?</h2><p className="mt-4 text-slate-400">We'll email you a secure link to set a new one.</p></div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} NewStarAds</p>
      </div>
      <div className="flex items-center justify-center p-6">
        {sent ? (
          <div className="w-full max-w-sm text-center" data-testid="forgot-success">
            <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-green-100 text-green-600 mb-4"><MailCheck className="w-7 h-7" /></div>
            <h1 className="font-display text-2xl font-bold">Check your email</h1>
            <p className="mt-2 text-slate-500 text-sm">If an account exists for <b>{email}</b>, we've sent a password reset link. It expires in 1 hour.</p>
            <Link to="/login"><Button variant="outline" className="mt-6 rounded-full">Back to login</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="forgot-form">
            <div><h1 className="font-display text-3xl font-bold tracking-tight">Reset password</h1><p className="mt-1 text-slate-500 text-sm">Enter your account email.</p></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full" data-testid="forgot-submit">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send reset link"}</Button>
            <p className="text-sm text-slate-500 text-center"><Link to="/login" className="text-primary font-medium">Back to login</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}
